import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
// iOS (all browsers there are WebKit) leaves stale snapshot bands — black bars
// above/below the gallery — when a view transition captures the top-layer
// <dialog> on zoom close (QA 09/07: Rich + Luke on real devices; not
// reproducible in Chromium or desktop-WebKit emulation). Gate the zoom
// transitions off on iOS: open/close is instant there instead.
// iPadOS 13+ masquerades as macOS, hence the maxTouchPoints check.
const IS_IOS =
  /iP(hone|ad|od)/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #hiddenMocks = /** @type {HTMLElement[]} */ ([]);
  #openedByPointer = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    // Don't open if clicking the View Similar button, Use this configuration button, or their children
    const target = event.target instanceof Element ? event.target : null;
    if (target) {
      // Check if target is the button itself or inside the button
      if (target.classList.contains('view-similar--v2') || target.closest('.view-similar--v2')) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }
      // Check if target is the select-button or inside it (Use this configuration)
      if (target.classList.contains('select-button') || target.closest('.select-button')) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }
    }

    event.preventDefault();

    // Keyboard-synthesized clicks have detail 0 / empty pointerType — track
    // modality so closeDialog() only drops restored focus for pointer users.
    this.#openedByPointer = (event?.detail ?? 0) > 0 || Boolean(/** @type {PointerEvent} */ (event)?.pointerType);

    const { dialog, media, thumbnails } = this.refs;

    // Guard: ensure refs are initialized
    if (!media || !Array.isArray(media) || media.length === 0) {
      console.error('Zoom dialog: No media elements found. Check ref="media[]" attributes.');
      return;
    }

    // Map index to actual zoom gallery index
    // If the index doesn't exist (e.g., clicking on a deduplicated mock), default to 0
    const mappedIndex = media[index] ? index : 0;
    const targetImage = media[mappedIndex];
    const targetThumbnail = thumbnails?.children[mappedIndex];

    const open = () => {
      // Hide all mocks in main gallery to prevent ResizeObserver crashes during pinch zoom
      this.#hideMainGalleryMocks();

      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }

      this.#positionPreviewNotice();
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    // Disable view transition if indices don't match (looks strange when transitioning to off-screen element)
    const useTransition = mappedIndex === index;

    // NB: supportsViewTransitions is a function — the old bare reference was
    // always truthy, so the no-transition fallback never fired anywhere.
    if (!supportsViewTransitions() || IS_IOS || !sourceImage || !targetImage || !useTransition) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(mappedIndex, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    // Clone the existing <img> so width/height/decoding etc. survive the
    // swap — a bare `new Image()` carried no dimensions, so the replacement
    // caused a layout shift while the high-res file decoded. srcset/sizes
    // are stripped deliberately: leaving them would let the browser keep
    // serving a responsive candidate instead of the max-resolution file
    // this swap exists to display.
    const newImage = /** @type {HTMLImageElement} */ (image.cloneNode(false));
    newImage.removeAttribute('srcset');
    newImage.removeAttribute('sizes');
    newImage.removeAttribute('loading');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;
    if (!media?.length) return;

    const mostVisibleElement = await getMostVisibleElement(media);
    if (!mostVisibleElement) return;
    const activeIndex = media.indexOf(mostVisibleElement);

    // Update thumbnails if they exist
    if (thumbnails) {
      const targetThumbnail = thumbnails.children[activeIndex];

      if (targetThumbnail && targetThumbnail instanceof HTMLElement) {
        Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
          button.setAttribute('aria-selected', `${i === activeIndex}`);
        });
      }
    }

    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    // Disable view transition if zoom gallery has fewer items than main gallery
    // (indicates deduplication, so indices don't match)
    const mainGalleryMedia = mediaGallery?.slideshow?.slides || mediaGallery?.media || [];
    const hasDeduplication = (media?.length ?? 0) < mainGalleryMedia.length;

    if (!media?.length || !supportsViewTransitions() || IS_IOS || hasDeduplication) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    // Wait for thumbnails animation if they exist
    if (this.refs.thumbnails) {
      await onAnimationEnd(this.refs.thumbnails);
    }

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();

    // iOS Safari treats the focus it restores after <dialog> close as
    // :focus-visible and paints the theme's global 3px currentcolor outline.
    // When that lands on a full-width gallery container, overflow-x clipping
    // leaves just the horizontal strokes — bare black lines above and below
    // the gallery (QA 09/07, Rich + Luke on device; invisible in Chromium,
    // which doesn't flag restored focus as focus-visible). Pointer users
    // don't need focus restoration, so drop it; keyboard flows keep theirs.
    if (this.#openedByPointer && IS_IOS) {
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && focused !== document.body) focused.blur();
    }

    // Restore hidden mocks after dialog closes
    this.#restoreMainGalleryMocks();

    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Positions the preview-quality notice vertically centered between
   * the bottom of the artwork image and the bottom of the viewport.
   */
  #positionPreviewNotice() {
    const notice = this.refs.dialog?.querySelector('.zoom-preview-notice');
    if (!notice) return;

    requestAnimationFrame(() => {
      const container = notice.closest('.product-media-container');
      if (!container) return;

      const img = container.querySelector('img.product-media__image');
      if (!img) return;

      const containerH = container.clientHeight;
      const imgRect = img.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Rendered image bottom relative to container bottom
      const imgBottomFromContainerBottom = containerRect.bottom - imgRect.bottom;
      // Center the notice in that gap, accounting for notice height
      const noticeH = notice.offsetHeight;
      const bottom = Math.max(12, imgBottomFromContainerBottom / 2 - noticeH / 2);

      notice.style.setProperty('--notice-bottom', `${bottom}px`);
    });
  }

  /**
   * Hides all mock elements in the main gallery to prevent ResizeObserver crashes.
   * Stores references to hidden elements for later restoration.
   */
  #hideMainGalleryMocks() {
    // Clear previous list
    this.#hiddenMocks = [];

    // Find the media gallery parent
    const mediaGallery = this.closest('media-gallery');
    if (!mediaGallery) return;

    // Find all mock elements in main gallery (not in zoom dialog)
    const mockElements = mediaGallery.querySelectorAll(
      'mock-image-css3d, mock-image-canvas, .mock-image-container'
    );

    // Defer style changes to next frame to avoid blocking interaction
    requestAnimationFrame(() => {
      // Filter out mocks that are inside the zoom dialog
      mockElements.forEach((mock) => {
        // Check if this mock is inside the zoom dialog (skip those)
        if (mock.closest('zoom-dialog')) return;

        // Store the mock and hide it
        this.#hiddenMocks.push(mock);
        mock.style.display = 'none';
      });
    });
  }

  /**
   * Restores all previously hidden mock elements in the main gallery.
   */
  #restoreMainGalleryMocks() {
    // Defer style changes to next frame to avoid blocking interaction
    requestAnimationFrame(() => {
      this.#hiddenMocks.forEach((mock) => {
        mock.style.display = '';
      });
      this.#hiddenMocks = [];
    });
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  // An empty list would observe nothing, so the callback never fires and the
  // promise never settles — hanging close()/handleScroll forever.
  if (!elements || elements.length === 0) return Promise.resolve(null);

  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        clearTimeout(fallbackTimer);
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    // Safety net: IO is spec'd to deliver an initial record per observed
    // element, but if delivery is ever suppressed (detached subtree, hidden
    // embedder), fall back to the first element rather than never settling.
    const fallbackTimer = setTimeout(() => {
      observer.disconnect();
      resolve(elements[0]);
    }, 250);

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
